import { useEffect, useState, useMemo } from 'react';
import { InfoCard } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import {
  CircularProgress,
  Typography,
  Chip,
  Box,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { AnimatedLineSeries, XYChart, Tooltip, Axis, Grid as VisxGrid } from '@visx/xychart';
import { curveMonotoneX } from '@visx/curve';

const METRICS = [
  'coverage',
  'reliability_rating',
  'security_rating',
  'sqale_rating',
] as const;

type MetricKey = (typeof METRICS)[number];

interface HistoryMeasure {
  metric: MetricKey;
  history: { date: string; value: string }[];
}

interface DataPoint {
  date: Date;
  value: number;
}

const METRIC_LABELS: Record<MetricKey, string> = {
  coverage: 'Coverage %',
  reliability_rating: 'Reliability',
  security_rating: 'Security',
  sqale_rating: 'Maintainability',
};

const RATING_LABELS: Record<number, string> = {
  1: 'A',
  2: 'B',
  3: 'C',
  4: 'D',
  5: 'E',
};

const useStyles = makeStyles(theme => ({
  loading: { display: 'flex', justifyContent: 'center', padding: theme.spacing(4) },
  empty: { padding: theme.spacing(2), color: theme.palette.text.secondary, textAlign: 'center' },
  chartContainer: { padding: theme.spacing(1, 2, 0, 2) },
  legend: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap' as const,
    padding: theme.spacing(1, 2),
    justifyContent: 'center',
  },
  summary: {
    display: 'flex',
    gap: theme.spacing(2),
    flexWrap: 'wrap' as const,
    padding: theme.spacing(1, 2, 2, 2),
    justifyContent: 'center',
  },
  summaryItem: {
    textAlign: 'center' as const,
    minWidth: 80,
  },
  summaryValue: {
    fontSize: '1.5rem',
    fontWeight: 700,
    lineHeight: 1.2,
  },
  summaryLabel: {
    fontSize: '0.7rem',
    color: theme.palette.text.secondary,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
  },
}));

const accessors = {
  xAccessor: (d: DataPoint) => d.date,
  yAccessor: (d: DataPoint) => d.value,
};

export const SonarQubeTrendCard = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { entity } = useEntity();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const projectKey = entity.metadata.annotations?.['sonarqube.org/project-key'];

  const [measures, setMeasures] = useState<HistoryMeasure[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!projectKey) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const baseUrl = await discoveryApi.getBaseUrl('quality-studio');
        const from = new Date();
        from.setDate(from.getDate() - 30);
        const params = new URLSearchParams({
          component: projectKey,
          metrics: METRICS.join(','),
          from: from.toISOString().split('T')[0],
          ps: '100',
        });
        const resp = await fetchApi.fetch(`${baseUrl}/measures/history?${params}`);
        if (!resp.ok) {
          setError('Failed to fetch quality history');
          return;
        }
        const data = await resp.json();
        setMeasures(data.measures ?? []);
      } catch {
        setError('SonarQube unavailable');
      } finally {
        setLoading(false);
      }
    })();
  }, [discoveryApi, fetchApi, projectKey]);

  const seriesData = useMemo(() => {
    const result: Partial<Record<MetricKey, DataPoint[]>> = {};
    for (const m of measures) {
      result[m.metric as MetricKey] = m.history.map(h => ({
        date: new Date(h.date),
        value: parseFloat(h.value),
      }));
    }
    return result;
  }, [measures]);

  const latestValues = useMemo(() => {
    const result: Partial<Record<MetricKey, number>> = {};
    for (const m of measures) {
      const last = m.history[m.history.length - 1];
      if (last) result[m.metric as MetricKey] = parseFloat(last.value);
    }
    return result;
  }, [measures]);

  const seriesColors: Record<MetricKey, string> = {
    coverage: theme.palette.type === 'dark' ? '#4CDD84' : '#187E3F',
    reliability_rating: theme.palette.type === 'dark' ? '#64B5F6' : '#1976D2',
    security_rating: theme.palette.type === 'dark' ? '#FFB74D' : '#F57C00',
    sqale_rating: theme.palette.type === 'dark' ? '#CE93D8' : '#7B1FA2',
  };

  const formatValue = (metric: MetricKey, value: number) => {
    if (metric === 'coverage') return `${value.toFixed(1)}%`;
    return RATING_LABELS[Math.round(value)] ?? value.toFixed(1);
  };

  if (!projectKey) return null;

  const coverageData = seriesData.coverage;
  const hasData = coverageData && coverageData.length > 1;

  return (
    <InfoCard title="Quality Trends" subheader="Last 30 days">
      {loading ? (
        <div className={classes.loading}><CircularProgress size={24} /></div>
      ) : error ? (
        <Typography className={classes.empty}>{error}</Typography>
      ) : !hasData ? (
        <Typography className={classes.empty}>
          Not enough history data for trends yet.
        </Typography>
      ) : (
        <>
          {/* ── Summary badges ──────────────────────────────── */}
          <div className={classes.summary}>
            {(Object.keys(METRIC_LABELS) as MetricKey[]).map(key => {
              const val = latestValues[key];
              if (val === undefined) return null;
              return (
                <div key={key} className={classes.summaryItem}>
                  <Typography
                    className={classes.summaryValue}
                    style={{ color: seriesColors[key] }}
                  >
                    {formatValue(key, val)}
                  </Typography>
                  <Typography className={classes.summaryLabel}>
                    {METRIC_LABELS[key]}
                  </Typography>
                </div>
              );
            })}
          </div>

          {/* ── Trend chart (coverage) ──────────────────────── */}
          <div className={classes.chartContainer}>
            <XYChart
              height={220}
              xScale={{ type: 'time' }}
              yScale={{ type: 'linear', domain: [0, 100], nice: true }}
            >
              <VisxGrid
                columns={false}
                numTicks={4}
                lineStyle={{
                  stroke: theme.palette.divider,
                  strokeDasharray: '2,3',
                }}
              />
              <Axis
                orientation="bottom"
                numTicks={5}
                tickFormat={(v: Date) =>
                  `${v.getDate()}/${v.getMonth() + 1}`
                }
                tickLabelProps={{
                  fill: theme.palette.text.secondary,
                  fontSize: 10,
                }}
              />
              <Axis
                orientation="left"
                numTicks={4}
                tickFormat={(v: number) => `${v}%`}
                tickLabelProps={{
                  fill: theme.palette.text.secondary,
                  fontSize: 10,
                }}
              />
              <AnimatedLineSeries
                dataKey="Coverage"
                data={coverageData}
                curve={curveMonotoneX}
                stroke={seriesColors.coverage}
                {...accessors}
              />
              <Tooltip
                snapTooltipToDatumX
                snapTooltipToDatumY
                showVerticalCrosshair
                renderTooltip={({ tooltipData }) => {
                  const datum = tooltipData?.nearestDatum?.datum as DataPoint | undefined;
                  if (!datum) return null;
                  return (
                    <Box p={0.5}>
                      <Typography variant="caption" display="block">
                        {datum.date.toLocaleDateString()}
                      </Typography>
                      <Typography variant="body2" style={{ fontWeight: 600 }}>
                        Coverage: {datum.value.toFixed(1)}%
                      </Typography>
                    </Box>
                  );
                }}
              />
            </XYChart>
          </div>

          {/* ── Rating trend charts ─────────────────────────── */}
          {(['reliability_rating', 'security_rating', 'sqale_rating'] as MetricKey[]).some(
            k => seriesData[k] && seriesData[k]!.length > 1,
          ) && (
            <div className={classes.chartContainer}>
              <XYChart
                height={140}
                xScale={{ type: 'time' }}
                yScale={{ type: 'linear', domain: [5, 1], nice: false }}
              >
                <VisxGrid
                  columns={false}
                  numTicks={5}
                  lineStyle={{
                    stroke: theme.palette.divider,
                    strokeDasharray: '2,3',
                  }}
                />
                <Axis
                  orientation="bottom"
                  numTicks={5}
                  tickFormat={(v: Date) =>
                    `${v.getDate()}/${v.getMonth() + 1}`
                  }
                  tickLabelProps={{
                    fill: theme.palette.text.secondary,
                    fontSize: 10,
                  }}
                />
                <Axis
                  orientation="left"
                  numTicks={5}
                  tickFormat={(v: number) => RATING_LABELS[Math.round(v)] ?? ''}
                  tickLabelProps={{
                    fill: theme.palette.text.secondary,
                    fontSize: 10,
                  }}
                />
                {(['reliability_rating', 'security_rating', 'sqale_rating'] as MetricKey[]).map(
                  key => {
                    const data = seriesData[key];
                    if (!data || data.length < 2) return null;
                    return (
                      <AnimatedLineSeries
                        key={key}
                        dataKey={METRIC_LABELS[key]}
                        data={data}
                        curve={curveMonotoneX}
                        stroke={seriesColors[key]}
                        {...accessors}
                      />
                    );
                  },
                )}
                <Tooltip
                  snapTooltipToDatumX
                  snapTooltipToDatumY
                  showVerticalCrosshair
                  renderTooltip={({ tooltipData }) => {
                    const datum = tooltipData?.nearestDatum?.datum as DataPoint | undefined;
                    const key = tooltipData?.nearestDatum?.key;
                    if (!datum) return null;
                    return (
                      <Box p={0.5}>
                        <Typography variant="caption" display="block">
                          {datum.date.toLocaleDateString()}
                        </Typography>
                        <Typography variant="body2" style={{ fontWeight: 600 }}>
                          {key}: {RATING_LABELS[Math.round(datum.value)] ?? datum.value}
                        </Typography>
                      </Box>
                    );
                  }}
                />
              </XYChart>
            </div>
          )}

          {/* ── Legend ───────────────────────────────────────── */}
          <div className={classes.legend}>
            {(Object.keys(METRIC_LABELS) as MetricKey[]).map(key => (
              <Chip
                key={key}
                label={METRIC_LABELS[key]}
                size="small"
                style={{
                  backgroundColor: seriesColors[key],
                  color: '#fff',
                  fontSize: '0.65rem',
                  height: 20,
                }}
              />
            ))}
          </div>
        </>
      )}
    </InfoCard>
  );
};
